import { createFileRoute } from "@tanstack/react-router";
import { getSharedDocument } from "@/lib/documents.functions";
import { SiteShell } from "@/components/site-shell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const Route = createFileRoute("/share/$token")({
  // Fetch on the server so og:title / og:description are SSR-rendered and
  // readable by social crawlers without JavaScript execution.
  loader: async ({ params }) => {
    const doc = await getSharedDocument({ data: { token: params.token } }).catch(() => null);
    return { doc };
  },
  head: ({ loaderData }) => {
    const doc = loaderData?.doc;
    if (!doc) {
      return {
        meta: [{ title: "Shared Document — DealDraft" }, { name: "robots", content: "noindex" }],
      };
    }
    const ogTitle = `${doc.project_title} — DealDraft`;
    const ogDescription = `Proposal and contract for ${doc.client_name} by ${doc.freelancer_name}. View the full document on DealDraft.`;
    return {
      meta: [
        { title: ogTitle },
        { name: "robots", content: "noindex" },
        { property: "og:title", content: ogTitle },
        { property: "og:description", content: ogDescription },
        { property: "og:image", content: "https://dealdraft.pro/og-image.png" },
        { property: "og:image:alt", content: `${doc.project_title} — DealDraft` },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: "DealDraft" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: ogTitle },
        { name: "twitter:description", content: ogDescription },
        { name: "twitter:image", content: "https://dealdraft.pro/og-image.png" },
      ],
    };
  },
  component: SharedDocumentPage,
});

function SharedDocumentPage() {
  const { doc } = Route.useLoaderData();
  if (!doc) {
    return (
      <SiteShell>
        <div className="mx-auto max-w-lg px-6 py-20 text-center">
          <h1 className="text-xl font-semibold">Share link not found</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            This link may have been revoked or never existed.
          </p>
        </div>
      </SiteShell>
    );
  }

  return (
    <SiteShell>
      <section
        className="mx-auto max-w-3xl px-6 py-10"
        data-testid="shared-document-view"
      >
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Shared via DealDraft
        </p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight">{doc.project_title}</h1>
        <p className="text-sm text-muted-foreground">
          {doc.freelancer_name} → {doc.client_name} · ${Number(doc.total_price).toLocaleString()}
        </p>

        <Tabs defaultValue="proposal" className="mt-6">
          <TabsList>
            <TabsTrigger value="proposal">Proposal</TabsTrigger>
            <TabsTrigger value="contract">Contract</TabsTrigger>
          </TabsList>
          <TabsContent value="proposal">
            <pre className="mt-4 whitespace-pre-wrap rounded-xl border border-border bg-card p-6 text-sm leading-relaxed shadow-sm">
              {doc.proposal_content}
            </pre>
          </TabsContent>
          <TabsContent value="contract">
            <pre className="mt-4 whitespace-pre-wrap rounded-xl border border-border bg-card p-6 text-sm leading-relaxed shadow-sm">
              {doc.contract_content}
            </pre>
          </TabsContent>
        </Tabs>
      </section>
    </SiteShell>
  );
}
